import React from "react";
import useDocumentTitle from "./ui/common/document-title";

const PrivacyPolicy: React.FC = () => {
  useDocumentTitle("Privacy Policy");
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-black">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>
        <p className="mb-4">
          At FitnFi.com, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use,
          and safeguard your personal information, including health and fitness data, and why we require specific data
          types.
        </p>
      </header>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">1. Information We Collect</h2>
        <p className="mb-4">
          We collect different types of personal data to provide, improve, and personalize our services. Below is a
          breakdown of the information we collect and why we need it.
        </p>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">User-Provided Data</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse mb-4">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4 bg-gray-50">Data Type</th>
                  <th className="text-left py-2 px-4 bg-gray-50">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 px-4">Identifiers (Name, Email, Phone Number, Username)</td>
                  <td className="py-2 px-4">
                    Used for account creation, communication, and personalization of user experience.
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Personal Information (Date of Birth, Gender)</td>
                  <td className="py-2 px-4">Helps in providing personalized fitness and health recommendations.</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Commercial Information (Transaction Details, Purchase History)</td>
                  <td className="py-2 px-4">
                    Enables processing of purchases, memberships, and other financial transactions.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">
            Health and Fitness Data (User-Provided or Fetched from Health Connect)
          </h3>
          <p className="mb-4">
            We collect health and fitness data either directly from users or by fetching it from Health Connect (with
            user consent). This data helps us provide accurate tracking, insights, and recommendations. Below is the
            justification for each requested data type:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse mb-4">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4 bg-gray-50">Data Type</th>
                  <th className="text-left py-2 px-4 bg-gray-50">Source</th>
                  <th className="text-left py-2 px-4 bg-gray-50">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 px-4">Steps</td>
                  <td className="py-2 px-4">User/Health Connect</td>
                  <td className="py-2 px-4">
                    Tracks daily activity levels, calculates progress, and personalized workout recommendations.
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Height</td>
                  <td className="py-2 px-4">User/Health Connect</td>
                  <td className="py-2 px-4">Essential for calculating BMI and personalizing fitness goals.</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Weight</td>
                  <td className="py-2 px-4">User/Health Connect</td>
                  <td className="py-2 px-4">Helps monitor weight changes and adjust fitness plans accordingly.</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Calories Burned</td>
                  <td className="py-2 px-4">Health Connect</td>
                  <td className="py-2 px-4">
                    Provides insights into energy expenditure to balance workouts and nutrition.
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4">Water Intake</td>
                  <td className="py-2 px-4">User/Health Connect</td>
                  <td className="py-2 px-4">
                    Tracks hydration levels and provides reminders for maintaining proper hydration.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">2. How We Use Your Information</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>To provide and personalize our services based on your fitness goals.</li>
          <li>To track and analyze your health and fitness progress.</li>
          <li>To communicate important updates and fitness insights.</li>
          <li>To improve our website and app functionalities.</li>
          <li>To comply with legal and regulatory obligations.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">3. Sharing Your Information</h2>
        <p className="mb-4">
          We do not sell your personal health data. However, we may share limited information with:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium">Service Providers:</span> For payment processing, cloud storage, and
            analytics.
          </li>
          <li>
            <span className="font-medium">Business Partners:</span> Aggregated and anonymized data may be shared for
            research and improvements.
          </li>
          <li>
            <span className="font-medium">Legal Compliance:</span> If required by law.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">4. Data Retention</h2>
        <p>
          We retain your personal information for as long as your account is active and for up to one year after account
          closure unless a longer retention period is legally required.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">5. Your Rights and Choices</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium">Access & Correction:</span> You can request access to or correction of your
            data.
          </li>
          <li>
            <span className="font-medium">Deletion:</span> You may request data deletion, subject to legal requirements.
          </li>
          <li>
            <span className="font-medium">Opt-out:</span> You can opt out of marketing communications.
          </li>
          <li>
            <span className="font-medium">Data Portability:</span> Request a copy of your data in a machine-readable
            format.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">6. Security of Your Information</h2>
        <p>
          We implement security measures to protect your personal information from unauthorized access, disclosure, and
          alteration. However, no method is 100% secure, so we recommend caution while sharing personal information
          online.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">7. Children's Privacy</h2>
        <p>
          Our services are not intended for individuals under 13. If we detect unauthorized data collection from minors,
          we will delete the information.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">8. Third-Party Services</h2>
        <p>
          We integrate with Health Connect, Google Fit, and Apple Health, but only access data with your explicit
          consent. We advise reviewing their privacy policies.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">9. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy. Changes will be communicated via our platform, and the "Effective Date"
          will be revised accordingly.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">10. Contact Us</h2>
        <p className="mb-4">For privacy concerns or inquiries, reach out to us at:</p>
        <p>
          Email:{" "}
          <a href="mailto:idev@q-sols.com" className="text-blue-600 hover:underline">
            idev@q-sols.com
          </a>
        </p>
      </section>
    </div>
  )
};

export default PrivacyPolicy;
